import axios from "axios";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { useAuth } from "../context/AuthContext";
import { auth, db } from "../firebase";

export default function Login() {
  const { currentUser, loading: authLoading } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);
  const [requestAccessData, setRequestAccessData] = useState({
    firstName: '',
    lastName: '',
    businessEmail: '',
    companySize: '',
    role: ''
  });
  const navigate = useNavigate();
  const DISABLE_AUTH_DEV = true;

  useEffect(() => {
    if (
      DISABLE_AUTH_DEV &&
      process.env.NODE_ENV === "development" &&
      !authLoading &&
      currentUser
    ) {
      navigate("/dashboard", { replace: true });
    }
  }, [currentUser, authLoading, navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRequestAccessSubmit = async (e) => {
    e.preventDefault();
    try {
      // Send request to backend
      const response = await fetch('/api/request-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestAccessData),
      });

      if (response.ok) {
        alert('Thank you! Your access request has been submitted. We\'ll review it within 24 hours.');
        setShowRequestAccessModal(false);
        setRequestAccessData({
          firstName: '',
          lastName: '',
          businessEmail: '',
          companySize: '',
          role: ''
        });
      } else {
        alert('There was an error submitting your request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('There was an error submitting your request. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setRequestAccessData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, form.email, form.password);
      const idToken = await userCredential.user.getIdToken();

      // Fetch user role from Firestore
      const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
      const userData = userDoc.data();
      const userRole = userData?.role || "client"; // Default to "client" for CI Agent

      // Store the ID token and user info
      localStorage.setItem("token", idToken);
      localStorage.setItem("user", JSON.stringify({
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        role: userRole
      }));

      // Update client email in the credit system
      try {
        await axios.post(`${API_BASE_URL}/api/client/update-email`,
          { email: userCredential.user.email },
          {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          }
        );
        console.log('Client email updated successfully');
      } catch (error) {
        console.error('Failed to update client email:', error);
        // Don't block login if email update fails
      }

      // Redirect to dashboard
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Marketing Section */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          className="object-cover w-full h-full"
          src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80"
          alt="Professional woman on phone call"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/40"></div>

        {/* Marketing content */}
        <div className="absolute inset-0 flex flex-col justify-center p-12 text-white">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            Leverage the network to hire great talent
          </h1>
          <p className="text-lg lg:text-xl leading-relaxed max-w-lg">
            Hire the best talent anytime, anywhere and enabling recruiters to monetize their professional networks and empowering jobseekers to find the best career opportunities.
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-20 bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            {/* Back Button */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#4f4f4f] hover:text-[#f7911d] transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="text-sm font-medium">Back</span>
            </button>

            {/* Logo - Clickable */}
            <Link
              to="/"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 bg-[#f7911d] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">C</span>
              </div>
              <span className="text-xl font-semibold text-[#0a0908]">candid-i</span>
            </Link>
          </div>

          <button
            onClick={() => setShowRequestAccessModal(true)}
            className="text-[#0a0908] hover:text-[#f7911d] transition-colors font-medium"
          >
            Request Access
          </button>
        </div>

        {/* Login Form */}
        <div className="max-w-md mx-auto w-full">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#0a0908] mb-2">Login</h2>
            <p className="text-[#4f4f4f]">Make the most of your professional network</p>
          </div>

          {error && (
            <div className="mb-6 p-4 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#0a0908] mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f7911d] focus:border-transparent outline-none transition-colors"
                  placeholder="Insert your email address"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0a0908] mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f7911d] focus:border-transparent outline-none transition-colors"
                  placeholder="Type your password"
                  disabled={loading}
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-[#f7911d] bg-gray-100 border-gray-300 rounded focus:ring-[#f7911d] focus:ring-2"
                  />
                  <span className="ml-2 text-sm text-[#4f4f4f]">Remember me</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#f7911d] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#e67e0a] focus:ring-2 focus:ring-[#f7911d] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Signing in..." : "Login to Account"}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-[#4f4f4f]">
            Don't have an account?{' '}
            <button
              onClick={() => setShowRequestAccessModal(true)}
              className="text-[#f7911d] hover:text-[#e67e0a] font-medium transition-colors"
            >
              Request Access
            </button>
          </p>
        </div>

        {/* Footer */}
        <div className="absolute bottom-6 right-6">
          <div className="flex items-center gap-2 text-xs text-[#4f4f4f]">
            <span>Privacy</span>
            <span>•</span>
            <span>Terms</span>
          </div>
        </div>
      </div>

      {/* Request Access Modal */}
      {showRequestAccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-10 relative">
            {/* Close Button */}
            <button
              onClick={() => setShowRequestAccessModal(false)}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Header */}
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#0a0908] mb-3">Try Alice Your AI Recruiter</h2>
              <p className="text-[#4f4f4f] text-base">Request access to our AI-powered recruitment platform</p>
            </div>

            {/* Form */}
            <form onSubmit={handleRequestAccessSubmit} className="space-y-8">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-[#0a0908] mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={requestAccessData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f7911d] focus:border-transparent outline-none transition-colors"
                  placeholder="Enter your first name"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-[#0a0908] mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={requestAccessData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f7911d] focus:border-transparent outline-none transition-colors"
                  placeholder="Enter your last name"
                />
              </div>

              {/* Business Email */}
              <div>
                <label className="block text-sm font-medium text-[#0a0908] mb-2">
                  Business Email
                </label>
                <input
                  type="email"
                  value={requestAccessData.businessEmail}
                  onChange={(e) => handleInputChange('businessEmail', e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f7911d] focus:border-transparent outline-none transition-colors"
                  placeholder="your.email@company.com"
                />
              </div>

              {/* Company Size */}
              <div>
                <label className="block text-sm font-medium text-[#0a0908] mb-2">
                  Company Size
                </label>
                <select
                  value={requestAccessData.companySize}
                  onChange={(e) => handleInputChange('companySize', e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f7911d] focus:border-transparent outline-none transition-colors"
                >
                  <option value="">Select company size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-1000">201-1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-[#0a0908] mb-2">
                  Your Role
                </label>
                <select
                  value={requestAccessData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f7911d] focus:border-transparent outline-none transition-colors"
                >
                  <option value="">Select your role</option>
                  <option value="HR Manager">HR Manager</option>
                  <option value="Recruiter">Recruiter</option>
                  <option value="Talent Acquisition">Talent Acquisition</option>
                  <option value="CEO/Founder">CEO/Founder</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-black text-white py-4 px-8 rounded-lg font-semibold text-lg hover:bg-gray-800 transition-colors mt-8"
              >
                Request Access
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
